<template>

    <v-select v-model="targetId" :items="targetIdList" :item-title="vv => {
        const name = vv[0] ? prettyEntityName(entityMap[vv[0]]) : 'all';
        const total = vv[1]?.toFixed(0);
        const spawnStr = vv[3] > 0 ? new Date(vv[3] * 1000).toLocaleTimeString([], { hour12: false }) : '?';
        const dieStr = vv[4] > 0 ? new Date(vv[4] * 1000).toLocaleTimeString([], { hour12: false }) : 'alive';
        const entityRaceId = entityMap[vv[0]]?.raceId;
        const bossTag = vv[0] !== '' && trackedBosses.length > 0 && entityRaceId && trackedBosses.includes(entityRaceId) ? '⭐ ' : '';
        
        return `${bossTag}${name} ${total} (${spawnStr} → ${dieStr})`;
    }" :item-value="vv => vv[0]" class="ma-2" variant="outlined" density="compact" hide-details>
    </v-select>

    <v-expansion-panels multiple v-for="v in pcEntities" v-bind:key="v.id">
        <template v-if="v.totalDamage > 0">
            <v-expansion-panel>
                <v-expansion-panel-title>
                    <v-sheet>
                        {{ prettyEntityName(v.entity) }} {{ v.totalDamage.toFixed(0) }} {{ (100 * v.totalDamage /
                            allApplyDamage).toFixed(1) }}% dps {{ v.duration < 1 ? 0 : Math.round(v.totalDamage / v.duration) }} </v-sheet>

                </v-expansion-panel-title>
                <v-expansion-panel-text class="pa-3">
                    <v-sheet
                        v-for="[skillId, skillStat] in Object.entries(v.skills).sort(([, a], [, b]) => (b as any).total - (a as any).total)"
                        v-bind:key="skillId" class="d-flex">
                        <v-sheet width="32" class="mr-2">
                            <img width="32" height="32" :src='getSkillIconUrl((skillStat as any).id, region)' />
                        </v-sheet>
                        <v-sheet width="100%" class="mb-4">
                            <!-- 스킬 이미지 -->
                            <!-- 이름 -->
                            <v-sheet width="100%">
                                <span class="font-weight-bold" style="font-size: 1.1em;"><span v-if="(skillStat as any).pet" class="text-green-lighten-2">Pet: </span>{{ skillNameMap[(skillStat as any).id] || `Skill ${(skillStat as any).id}` }}</span>
                                damage: {{ (skillStat as any).total.toFixed(0) }}
                                count: {{ (skillStat as any).count }}
                                avgDamage: {{ ((skillStat as any).count ? (skillStat as any).total / (skillStat as any).count : 0).toFixed(0) }}
                                minDamage: {{ (skillStat as any).min > 9000000 ? 0 : (skillStat as any).min.toFixed(0) }}
                                maxDamage: {{ (skillStat as any).max?.toFixed(0) || '0' }}
                                {{ (100 * (skillStat as any).total / v.totalDamage).toFixed(1) }}%
                            </v-sheet>

                            <!-- 막대 -->
                            <v-sheet width="100%" height="16">
                                <v-sheet :color="getMabiNameColor(skillNameMap[(skillStat as any).id] || `unknownSkill:${skillId}`)"
                                    height="100%"
                                    :width="`${Math.round(100 * (skillStat as any).total / v.totalDamage).toFixed(0)}%`"
                                    class="rounded-xl">
                                </v-sheet>
                            </v-sheet>
                        </v-sheet>
                    </v-sheet>
                </v-expansion-panel-text>
            </v-expansion-panel>
            <!-- 막대 -->
            <v-sheet width="100%" height="16">
                <v-sheet :color="getMabiNameColor(prettyEntityName(v.entity)!)" height="100%"
                    :width="`${Math.round(100 * v.totalDamage / allApplyDamage).toFixed(0)}%`" class="rounded-xl">
                </v-sheet>
            </v-sheet>
        </template>
    </v-expansion-panels>
</template>
<script lang="ts">
import { defineComponent, inject, ref, computed, onUnmounted, onMounted } from "vue";
import { getPrimaryClass, anonymizeMode, trackedBosses, liveSyncState } from '@/store';
import { getMabiNameColor, getSkillIconUrl } from '@/util';
import { ActorManager, BaseActor, GroupActor, EntityActor } from '@/eventActor';

export default defineComponent({
    setup() {
        const isLoading = inject('isLoading');
        const region = inject('region');
        const raceNameMap = inject('raceNameMap') as any;
        const skillNameMap = inject('skillNameMap') as any;
        const appEvent = inject('appEvent') as any;
        const actorManager = inject('actorManager') as any;

        const clearTarget = () => { targetId.value = ''; }
        onMounted(() => appEvent.value.addEventListener('clear', clearTarget));
        onUnmounted(() => appEvent.value.removeEventListener('clear', clearTarget));

        const targetId = ref('');

        const entityMap = computed(() => {
            appEvent.value;
            actorManager.value.liveUpdate.value;
            return actorManager.value.entityMap as Record<string, EntityActor>;
        });

        const targetIdList = computed(() => {
            const list: [string, number, number, number, number][] = [];
            let fullRaidTotal = 0;
            
            for (const key of Object.keys(entityMap.value)) {
                const entity = entityMap.value[key] as any;
                if (entity?.isPC || (entity?.ownerId && entity.ownerId !== "0")) continue;

                let targetTotal = 0;
                let lastAt = entity.lastTakeDamageAt || 0;

                // Tabulate all damages applied TO this target by PCs
                for (const pc of Object.values(entityMap.value).filter(v => v.isPC)) {
                    const stats = (pc as any).aggregatedStats?.[key];
                    if (stats) {
                        targetTotal += stats.totalDamage;
                        if (stats.lastAttackAt > lastAt) lastAt = stats.lastAttackAt;
                    }
                }

                if (targetTotal > 0) {
                    list.push([key, targetTotal, lastAt, entity.appearAt || 0, entity.disappearAt || lastAt]);
                    fullRaidTotal += targetTotal;
                }
            }

            list.sort((a, b) => {
                if (b[4] !== a[4]) return b[4] - a[4];
                return b[1] - a[1];
            });

            list.unshift(['', fullRaidTotal, 0, 0, 0]);
            return list;
        });

        const pcEntities = computed(() => {
            const pcList = Object.values(entityMap.value).filter(v => v.isPC).map((pc: any) => {
                let totalDamage = 0;
                let skills: Record<string, any> = {};
                let firstAttackAt = 99999999;
                let lastAttackAt = 0;
                // Pet skills share ids with the player's own (Combat Mastery),
                // so they are listed under "pet:<id>" keys as their own rows.
                const addSkills = (src: Record<string, any> | undefined, prefix: string) => {
                    for (const [sk, sdata] of Object.entries(src || {})) {
                        const sd = sdata as any;
                        const key = prefix + sk;
                        if (!skills[key]) skills[key] = { id: sd.id, pet: !!prefix, total: 0, count: 0, min: 99999999, max: 0 };
                        skills[key].total += sd.total;
                        skills[key].count += sd.count;
                        if (sd.min < skills[key].min) skills[key].min = sd.min;
                        if (sd.max > skills[key].max) skills[key].max = sd.max;
                    }
                };

                if (targetId.value) {
                    const stats = pc.aggregatedStats?.[targetId.value];
                    if (stats) {
                        totalDamage = stats.totalDamage;
                        addSkills(stats.skills, '');
                        addSkills(stats.petSkills, 'pet:');
                        firstAttackAt = stats.firstAttackAt;
                        lastAttackAt = stats.lastAttackAt;
                    }
                } else {
                    // Aggregate all targets
                    totalDamage = pc._totalApplyDamage || 0;
                    for (const stats of Object.values(pc.aggregatedStats || {})) {
                        const tstats = stats as any;
                        if (tstats.firstAttackAt < firstAttackAt) firstAttackAt = tstats.firstAttackAt;
                        if (tstats.lastAttackAt > lastAttackAt) lastAttackAt = tstats.lastAttackAt;
                        
                        addSkills(tstats.skills, '');
                        addSkills(tstats.petSkills, 'pet:');
                    }
                }
                
                let duration = (lastAttackAt && firstAttackAt < 99999999) ? (lastAttackAt - firstAttackAt) : 0;
                
                // Live Fallback: If duration is 0 or we are viewing 'all' targets, 
                // use the overall combatDuration from the live sync state if available.
                if ((!targetId.value || duration <= 0) && liveSyncState.value?.combatDuration > duration) {
                    duration = liveSyncState.value.combatDuration;
                }

                return {
                    id: pc.id,
                    entity: pc,
                    name: pc.name,
                    raceId: pc.raceId,
                    totalDamage,
                    skills,
                    duration: duration
                };
            });

            return pcList.sort((a, b) => b.totalDamage - a.totalDamage);
        });

        const allApplyDamage = computed(() =>
            pcEntities.value.reduce((acc, v) => acc + v.totalDamage, 0));

        const prettyEntityName = (entity?: BaseActor) => {
            if (!entity) return undefined;
            if (ActorManager.pcRaceSet.has(entity.raceId)) {
                const cls = getPrimaryClass(entity as any);
                if (anonymizeMode.value) return cls ? `[${cls}]` : `[Unknown]`;
                return `${entity.name}${cls ? ` [${cls}]` : ''}`;
            }
            const raceName = raceNameMap.value[entity.raceId] || `unknownRace:${entity.raceId}`;
            if (entity instanceof GroupActor) return raceName;
            if (entity.name[0] >= '0' && entity.name[0] <= '9') return `${raceName} (${entity.raceId})`;
            return entity.name;
        }

        return {
            isLoading,
            region,
            pcEntities,
            allApplyDamage,
            targetId,
            targetIdList,
            skillNameMap,
            entityMap,
            getMabiNameColor,
            getSkillIconUrl,
            prettyEntityName,
            trackedBosses
        }
    }
});
</script>